import { TipCardConfig, TipCardSection, TipCardState } from '../types/tipcard';

export interface TipCardPlacement {
  tipId: string;
  config: TipCardConfig;
  section: TipCardSection;
  indexInSection: number; // Position within the section (0-based)
}

/**
 * Calculate where TipCards should be placed in the feed
 * Follows these rules:
 * 1. "first-in-section" tips appear immediately after section header (index 0)
 * 2. "listed-flow" tips flow across Para você → Outros pedidos sections
 * 3. First listed tip appears after 5 items minimum
 * 4. Minimum 5 items spacing between listed tips
 * 5. Express section is excluded from tip placement
 * 6. Each tip maintains its absolute position even when others are dismissed
 * 7. A listed tip com `listedSection` só ocupa slots daquela seção; sem ele,
 *    ocupa qualquer slot do fluxo (Para você ou Outros). `listOrder` define a
 *    prioridade na disputa pelos slots.
 */
export function calculateTipCardPlacements(
  configs: TipCardConfig[],
  tipCardState: TipCardState,
  sectionItemCounts: {
    destaque: number;
    para_voce: number;
    outros: number;
  }
): {
  firstInSection: Record<TipCardSection, TipCardPlacement | null>;
  listedFlow: TipCardPlacement[];
} {
  const firstInSection: Record<TipCardSection, TipCardPlacement | null> = {
    destaque: null,
    para_voce: null,
    outros: null,
  };
  
  const listedFlow: TipCardPlacement[] = [];

  // Filter enabled tips (including dismissed ones for position calculation)
  const enabledTips = configs.filter((config) => config.enabled);

  // Process "first-in-section" tips (only show non-dismissed)
  const firstInSectionTips = enabledTips.filter(
    (config) => config.placementMode === 'first-in-section' && config.section && !tipCardState[config.id]?.dismissed
  );

  firstInSectionTips.forEach((config) => {
    const section = config.section as TipCardSection;
    // Only place in sections that have items (and never in Express)
    if (section !== 'destaque' && sectionItemCounts[section] > 0) {
      firstInSection[section] = {
        tipId: config.id,
        config,
        section,
        indexInSection: 0, // Always first item in section
      };
    } else if (section === 'destaque' && sectionItemCounts.destaque > 0) {
      firstInSection.destaque = {
        tipId: config.id,
        config,
        section: 'destaque',
        indexInSection: 0,
      };
    }
  });

  // Process "listed-flow" tips
  // Get ALL listed tips (dismissed and active) sorted by listOrder.
  // listOrder define a PRIORIDADE na disputa pelos slots disponíveis.
  const allListedTips = enabledTips
    .filter((config) => config.placementMode === 'listed-flow')
    .sort((a, b) => (a.listOrder || 0) - (b.listOrder || 0));

  if (allListedTips.length === 0) {
    return { firstInSection, listedFlow };
  }

  // Monta os "slots" disponíveis para tips no fluxo: um slot a cada 5 itens,
  // percorrendo Para você → Outros (Express nunca recebe listed-flow). Cada
  // slot guarda a seção a que pertence, para respeitar a restrição de seção.
  const flowSections: Array<Extract<TipCardSection, 'para_voce' | 'outros'>> = [
    'para_voce',
    'outros',
  ];
  const slots: Array<{
    section: TipCardSection;
    indexInSection: number;
    continuousIndex: number;
  }> = [];

  let continuousItemIndex = 0;
  flowSections.forEach((section) => {
    const itemCount = sectionItemCounts[section];

    for (let itemIndexInSection = 0; itemIndexInSection < itemCount; itemIndexInSection++) {
      // O tip é renderizado ANTES do card neste índice, então abrir um slot
      // quando já passaram 5, 10, 15... itens faz o tip aparecer depois de
      // cada grupo de 5 itens (primeiro depois do 5º, e a cada 5 em seguida).
      if (continuousItemIndex > 0 && continuousItemIndex % 5 === 0) {
        slots.push({
          section,
          indexInSection: itemIndexInSection,
          continuousIndex: continuousItemIndex,
        });
      }

      continuousItemIndex++;
    }
  });

  // Aloca cada tip (na ordem de listOrder) no primeiro slot livre cuja seção
  // seja elegível. Tips com `listedSection` só entram naquela seção; sem ele,
  // entram em qualquer seção do fluxo. Tips dispensados TAMBÉM consomem seu
  // slot, para manter as posições estáveis quando outros são dispensados.
  const usedSlots = new Set<number>();
  const tipPositions: Array<{
    tip: TipCardConfig;
    section: TipCardSection;
    indexInSection: number;
  }> = [];

  allListedTips.forEach((tip) => {
    const eligibleSections: TipCardSection[] = tip.listedSection
      ? [tip.listedSection]
      : ['para_voce', 'outros'];

    const slot = slots.find(
      (s) =>
        !usedSlots.has(s.continuousIndex) &&
        eligibleSections.includes(s.section)
    );

    if (slot) {
      usedSlots.add(slot.continuousIndex);
      tipPositions.push({
        tip,
        section: slot.section,
        indexInSection: slot.indexInSection,
      });
    }
  });

  // Now filter to only include non-dismissed tips for actual rendering
  // (as posições já são estáveis pois todos os tips consumiram seus slots).
  tipPositions.forEach((pos) => {
    if (!tipCardState[pos.tip.id]?.dismissed) {
      listedFlow.push({
        tipId: pos.tip.id,
        config: pos.tip,
        section: pos.section,
        indexInSection: pos.indexInSection,
      });
    }
  });

  return { firstInSection, listedFlow };
}

/**
 * Check if a TipCard should be rendered at a specific position
 */
export function shouldRenderTipAt(
  section: TipCardSection,
  itemIndex: number,
  placements: ReturnType<typeof calculateTipCardPlacements>
): TipCardPlacement | null {
  // Check first-in-section
  const firstInSectionTip = placements.firstInSection[section];
  if (firstInSectionTip && firstInSectionTip.indexInSection === itemIndex) {
    return firstInSectionTip;
  }

  // Check listed-flow
  const listedTip = placements.listedFlow.find(
    (placement) => 
      placement.section === section && 
      placement.indexInSection === itemIndex
  );
  
  return listedTip || null;
}
